/**
 * Dashboard de vendas.
 *
 * ## Por que os gráficos são feitos à mão
 *
 * O plano da Fase 2 previa ECharts com importação seletiva. Medi antes de
 * instalar: o bundle do gestor está em 88 KB gzip, e o ECharts por módulo pesa
 * perto de 100 KB — dobraria a tela de quem abre o portal no celular no meio do
 * turno.
 *
 * E o que esta frente precisa não justifica isso: uma linha de UMA série, uma
 * grade 7×24 e barras horizontais. Grade é CSS. Barra é div com largura. Linha
 * é um `polyline`. Feito à mão, custa 0 KB, usa os tokens da marca direto e dá
 * controle do hover e da acessibilidade — que a biblioteca também exigiria
 * configurar. Se uma frente adiante precisar de algo de verdade difícil, a
 * decisão se revisita com a medição na mão.
 *
 * ## As cores não foram escolhidas, foram medidas
 *
 * Ver o bloco "Paleta de GRÁFICO" em packages/ui/src/tokens.css: a rampa do
 * mapa de calor passou pelos testes de rampa ordinal (luminosidade monótona,
 * ΔL >= 0,06 entre passos, ponta clara a 2,29:1 sobre o branco).
 *
 * Barra de categoria nominal (canal, tipo, pagamento) usa TODAS o mesmo tom: o
 * comprimento da barra já codifica o valor, e colorir cada uma de um jeito
 * gastaria o canal de identidade mostrando o que já está mostrado. Classe ABC é
 * ORDINAL (A vale mais que C), então ela usa a rampa — não cores diferentes.
 */
import { formatarReais } from '@du/shared'
import { Badge, Button, EmptyState, Icone, Input, SectionTitle, Skeleton, StatTile } from '@du/ui'
import { ArrowDownRight, ArrowRight, ArrowUpRight, Table2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api.js'

// ─── O que a API devolve ────────────────────────────────────────────────────

type Variacao = { atual: number; anterior: number; variacaoPct: number | null }
type Fatia = { chave: string; pedidos: number; faturamentoCents: number; fatiaPct: number }
type PontoDia = { dia: string; pedidos: number; faturamentoCents: number; ticketMedioCents: number }
type Celula = { diaSemana: number; hora: number; pedidos: number; faturamentoCents: number }
type ItemAbc = {
  produtoId: string | null
  nome: string
  quantidade: number
  faturamentoCents: number
  fatiaPct: number
  acumuladoPct: number
  classe: 'A' | 'B' | 'C'
}
type Origem = {
  origem: string
  nome: string
  acessos: number
  pedidos: number
  entregues: number
  conversaoPct: number | null
  faturamentoCents: number
}

type Dados = {
  periodo: { de: string; ate: string; dias: number; ehHoje: boolean }
  resumo: {
    pedidos: {
      total: number
      entregues: number
      cancelados: number
      emAndamento: number
      cancelamentoPct: number
    }
    faturamentoCents: number
    ticketMedioCents: number
    mediaDiariaCents: number
    composicao: { produtosCents: number; taxasCents: number; descontosCents: number }
    perdidoCents: number
    inconsistentes: number
  }
  comparacao: {
    anterior: { de: string; ate: string }
    faturamento: Variacao
    pedidos: Variacao
    ticketMedio: Variacao
  }
  serie: PontoDia[]
  calor: Celula[]
  porCanal: Fatia[]
  porTipo: Fatia[]
  porPagamento: Fatia[]
  porBairro: Fatia[]
  abc: ItemAbc[]
  origens: Origem[]
  entrega: { distanciaMediaKm: number | null; pedidosComDistancia: number }
}

// ─── Apoio ──────────────────────────────────────────────────────────────────

const hoje = () => new Date().toISOString().slice(0, 10)

function somarDias(dia: string, dias: number): string {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(a, m - 1, d) + dias * 86_400_000).toISOString().slice(0, 10)
}

const ddmm = (dia: string) => dia.slice(8, 10) + '/' + dia.slice(5, 7)

const NOME_DIA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const ROTULO: Record<string, string> = {
  SITE: 'Site', WHATSAPP: 'WhatsApp', BALCAO: 'Balcão', IA: 'Atendente IA',
  ENTREGA: 'Entrega', RETIRADA: 'Retirada',
  DINHEIRO: 'Dinheiro', CARTAO_ENTREGA: 'Cartão na entrega',
  PIX_MANUAL: 'Pix manual', PIX_ONLINE: 'Pix online',
}
const nomear = (c: string) => ROTULO[c] ?? c

/**
 * A variação ao lado do número.
 *
 * `variacaoPct: null` quer dizer que o período anterior foi zero — crescer de 0
 * para 1 não é "+100%", é a primeira venda. Mostra "—" em vez de inventar.
 */
function Variar({ v, dinheiro = false }: { v: Variacao; dinheiro?: boolean }) {
  const fmt = (n: number) => (dinheiro ? formatarReais(n) : String(n))
  if (v.variacaoPct === null) {
    return <span className="dash-var dash-var--neutro">— vs {fmt(v.anterior)}</span>
  }
  const sobe = v.variacaoPct > 0
  const igual = v.variacaoPct === 0
  return (
    <span className={`dash-var dash-var--${igual ? 'neutro' : sobe ? 'sobe' : 'cai'}`}>
      {/* Seta + sinal: a direção nunca depende só da cor. */}
      <Icone de={igual ? ArrowRight : sobe ? ArrowUpRight : ArrowDownRight} tamanho={14} />
      {igual ? '0%' : `${sobe ? '+' : ''}${v.variacaoPct}%`}
      <span className="dash-var__base">vs {fmt(v.anterior)}</span>
    </span>
  )
}

// ─── Linha de tendência ─────────────────────────────────────────────────────

/**
 * Faturamento por dia. UMA série, então não leva legenda — o título diz o que é.
 *
 * Tem crosshair e tooltip por padrão: um gráfico numa página é interativo, e sem
 * isso o leitor não consegue ler o valor de um dia específico. Os pontos não
 * levam rótulo um por um; só o maior e o último, que é o que alguém procura.
 */
function Tendencia({ serie, parcial }: { serie: PontoDia[]; parcial: boolean }) {
  const [sobre, setSobre] = useState<number | null>(null)
  const svg = useRef<SVGSVGElement>(null)

  const L = 56, R = 16, T = 16, B = 28
  const W = 760, H = 220
  const largura = W - L - R
  const altura = H - T - B

  const maximo = Math.max(...serie.map((p) => p.faturamentoCents), 1)
  const x = (i: number) => L + (serie.length === 1 ? largura / 2 : (i / (serie.length - 1)) * largura)
  const y = (v: number) => T + altura - (v / maximo) * altura

  const pontos = serie.map((p, i) => `${x(i)},${y(p.faturamentoCents)}`).join(' ')
  const iMaior = serie.reduce((m, p, i) => (p.faturamentoCents > (serie[m]?.faturamentoCents ?? 0) ? i : m), 0)

  const mover = (e: React.MouseEvent<SVGSVGElement>) => {
    const caixa = svg.current?.getBoundingClientRect()
    if (!caixa || serie.length === 0) return
    const px = ((e.clientX - caixa.left) / caixa.width) * W
    const i = Math.round(((px - L) / largura) * (serie.length - 1))
    setSobre(Math.min(serie.length - 1, Math.max(0, i)))
  }

  // Quatro linhas de grade com valor: o suficiente para ler a ordem de
  // grandeza sem a grade competir com os dados.
  const grades = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ f, valor: Math.round(maximo * f) }))
  const atual = sobre === null ? null : serie[sobre]

  return (
    <div className="dash-grafico">
      <svg
        ref={svg}
        viewBox={`0 0 ${W} ${H}`}
        className="dash-linha"
        role="img"
        aria-label={`Faturamento por dia. Maior dia: ${ddmm(serie[iMaior]?.dia ?? '')} com ${formatarReais(serie[iMaior]?.faturamentoCents ?? 0)}.`}
        onMouseMove={mover}
        onMouseLeave={() => setSobre(null)}
      >
        {grades.map((g) => (
          <g key={g.f}>
            <line
              x1={L} x2={W - R}
              y1={T + altura - g.f * altura} y2={T + altura - g.f * altura}
              className="dash-grade"
            />
            <text x={L - 8} y={T + altura - g.f * altura + 4} className="dash-eixo" textAnchor="end">
              {g.valor === 0 ? '0' : formatarReais(g.valor).replace('R$ ', '')}
            </text>
          </g>
        ))}

        {/* O dia em curso ainda vai receber pedidos: a última perna fica
            tracejada para ninguém comparar um dia incompleto com um fechado. */}
        <polyline points={pontos} className="dash-linha__traco" />
        {parcial && serie.length > 1 ? (
          <polyline
            points={serie.slice(-2).map((p, k) => `${x(serie.length - 2 + k)},${y(p.faturamentoCents)}`).join(' ')}
            className="dash-linha__traco dash-linha__traco--parcial"
          />
        ) : null}

        {/* Rótulo direto só no maior dia. Número em todo ponto é ruído. */}
        {serie.length > 1 ? (
          <text x={x(iMaior)} y={y(serie[iMaior]?.faturamentoCents ?? 0) - 10} className="dash-rotulo" textAnchor="middle">
            {formatarReais(serie[iMaior]?.faturamentoCents ?? 0)}
          </text>
        ) : null}

        {/* Um rótulo a cada N dias, mais o último. O `-2` evita o caso em que
            o rótulo do passo cai colado no último e os dois se sobrepõem —
            "08/1@9/10" na tela. */}
        {serie.map((p, i) =>
          (i % Math.ceil(serie.length / 8) === 0 && i < serie.length - 2) ||
          i === serie.length - 1 ? (
            <text key={p.dia} x={x(i)} y={H - 8} className="dash-eixo" textAnchor="middle">
              {ddmm(p.dia)}
            </text>
          ) : null,
        )}

        {atual && sobre !== null ? (
          <g>
            <line x1={x(sobre)} x2={x(sobre)} y1={T} y2={T + altura} className="dash-crosshair" />
            <circle cx={x(sobre)} cy={y(atual.faturamentoCents)} r={5} className="dash-ponto" />
          </g>
        ) : null}
      </svg>

      {atual ? (
        <div className="dash-dica" role="status">
          <strong>{ddmm(atual.dia)}</strong> · {formatarReais(atual.faturamentoCents)} ·{' '}
          {atual.pedidos} {atual.pedidos === 1 ? 'pedido' : 'pedidos'}
          {atual.pedidos > 0 ? ` · ticket ${formatarReais(atual.ticketMedioCents)}` : ''}
        </div>
      ) : (
        <div className="dash-dica dash-dica--vazia">Passe o mouse para ver o dia</div>
      )}
    </div>
  )
}

// ─── Mapa de calor ──────────────────────────────────────────────────────────

/**
 * Dia da semana × hora. Grade CSS, uma div por célula.
 *
 * O dia da semana vem do DIA OPERACIONAL, não da data civil: o pedido de 00h30
 * de sábado é da noite de sexta, e é assim que a loja pensa. Por isso a célula
 * "Sex, 0h" existe e não é erro.
 *
 * Célula sem venda não recebe tom: ela é a própria superfície. Cinco passos
 * bastam — mais degraus num mapa pequeno viram ruído.
 */
function MapaCalor({ calor }: { calor: Celula[] }) {
  const [sobre, setSobre] = useState<Celula | null>(null)
  const maximo = Math.max(...calor.map((c) => c.pedidos), 1)
  const porChave = new Map(calor.map((c) => [`${c.diaSemana}-${c.hora}`, c]))

  // Só as horas com movimento, para a grade não ter 24 colunas vazias.
  const horas = [...new Set(calor.map((c) => c.hora))].sort((a, b) => a - b)
  if (horas.length === 0) return <EmptyState titulo="Nenhuma venda no período" />

  const passo = (pedidos: number) => Math.min(5, Math.max(1, Math.ceil((pedidos / maximo) * 5)))

  return (
    <div className="dash-calor">
      <div className="dash-calor__grade" style={{ gridTemplateColumns: `3rem repeat(${horas.length}, 1fr)` }}>
        <div />
        {horas.map((h) => (
          <div key={h} className="dash-calor__hora">{h}h</div>
        ))}
        {NOME_DIA.map((nome, d) => (
          <div key={nome} style={{ display: 'contents' }}>
            <div className="dash-calor__dia">{nome}</div>
            {horas.map((h) => {
              const c = porChave.get(`${d}-${h}`)
              return (
                <div
                  key={h}
                  className={c ? `dash-calor__celula dash-calor__celula--${passo(c.pedidos)}` : 'dash-calor__celula'}
                  onMouseEnter={() => setSobre(c ?? null)}
                  onMouseLeave={() => setSobre(null)}
                  title={
                    c
                      ? `${nome}, ${h}h: ${c.pedidos} ${c.pedidos === 1 ? 'pedido' : 'pedidos'}, ${formatarReais(c.faturamentoCents)}`
                      : `${nome}, ${h}h: sem venda`
                  }
                  aria-label={c ? `${nome} ${h} horas, ${c.pedidos} pedidos` : undefined}
                />
              )
            })}
          </div>
        ))}
      </div>
      <div className="dash-calor__pe">
        <div className="dash-dica" role="status">
          {sobre
            ? `${NOME_DIA[sobre.diaSemana]}, ${sobre.hora}h · ${sobre.pedidos} ${sobre.pedidos === 1 ? 'pedido' : 'pedidos'} · ${formatarReais(sobre.faturamentoCents)}`
            : 'Passe o mouse numa célula'}
        </div>
        <div className="dash-escala" aria-hidden="true">
          <span className="dash-escala__rotulo">menos</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`dash-escala__passo dash-calor__celula--${n}`} />
          ))}
          <span className="dash-escala__rotulo">mais</span>
        </div>
      </div>
    </div>
  )
}

// ─── Barras ─────────────────────────────────────────────────────────────────

/**
 * Barras horizontais com rótulo direto.
 *
 * Todas do MESMO tom, de propósito: o comprimento já codifica o valor, e dar
 * uma cor a cada categoria gastaria o canal de identidade repetindo o que a
 * barra mostra. Horizontal porque os nomes são textos ("Cartão na entrega") e
 * caberiam deitados no eixo de um gráfico vertical.
 */
function Barras({
  fatias,
  aoClicar,
}: {
  fatias: Fatia[]
  aoClicar?: ((chave: string) => void) | undefined
}) {
  if (fatias.length === 0) return <EmptyState titulo="Sem dados no período" />
  const maximo = Math.max(...fatias.map((f) => f.faturamentoCents), 1)

  return (
    <ul className="dash-barras">
      {fatias.map((f) => (
        <li key={f.chave} className="dash-barra">
          <button
            type="button"
            className="dash-barra__alvo"
            onClick={aoClicar ? () => aoClicar(f.chave) : undefined}
            disabled={!aoClicar}
          >
            <span className="dash-barra__nome">{nomear(f.chave)}</span>
            <span className="dash-barra__trilha">
              <span
                className="dash-barra__preenchimento"
                style={{ width: `${Math.max(2, (f.faturamentoCents / maximo) * 100)}%` }}
              />
            </span>
            <span className="dash-barra__valor">
              {formatarReais(f.faturamentoCents)}
              <span className="dash-barra__apoio">
                {f.fatiaPct}% · {f.pedidos} {f.pedidos === 1 ? 'pedido' : 'pedidos'}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

// ─── A tela ─────────────────────────────────────────────────────────────────

const PRESETS = [
  { rotulo: '7 dias', dias: 7 },
  { rotulo: '30 dias', dias: 30 },
  { rotulo: '90 dias', dias: 90 },
] as const

export function Dashboard() {
  const [ate, setAte] = useState(hoje())
  const [de, setDe] = useState(somarDias(hoje(), -29))
  const [dados, setDados] = useState<Dados | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [tabela, setTabela] = useState(false)

  const buscar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setDados(await api<Dados>(`/v1/dashboard?de=${de}&ate=${ate}`))
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar o dashboard.')
    } finally {
      setCarregando(false)
    }
  }, [de, ate])

  useEffect(() => {
    void buscar()
  }, [buscar])

  const preset = (dias: number) => {
    const fim = hoje()
    setAte(fim)
    setDe(somarDias(fim, -(dias - 1)))
  }

  const r = dados?.resumo
  const totalAbc = useMemo(
    () => dados?.abc.reduce((s, i) => s + i.faturamentoCents, 0) ?? 0,
    [dados],
  )

  return (
    <div className="pagina">
      <SectionTitle
        tamanho="sm"
        acao={
          <Button
            variante="ghost"
            tamanho="sm"
            icone={Table2}
            onClick={() => setTabela((v) => !v)}
          >
            {tabela ? 'Ver gráficos' : 'Ver como tabela'}
          </Button>
        }
      >
        Vendas
      </SectionTitle>

      {/* Filtros numa linha só, acima dos gráficos. */}
      <div className="dash-filtros">
        {PRESETS.map((p) => (
          <Button
            key={p.dias}
            variante={dados?.periodo.dias === p.dias ? 'primario' : 'ghost'}
            tamanho="sm"
            onClick={() => preset(p.dias)}
          >
            {p.rotulo}
          </Button>
        ))}
        <Input rotulo="De" type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} />
        <Input rotulo="Até" type="date" value={ate} max={hoje()} min={de} onChange={(e) => setAte(e.target.value)} />
      </div>

      {erro ? (
        <EmptyState titulo="Não deu para carregar" texto={erro} acao={<Button onClick={() => void buscar()}>Tentar de novo</Button>} />
      ) : carregando || !dados || !r ? (
        <Skeleton altura={420} />
      ) : (
        <>
          {/* Dinheiro que não fecha nunca é escondido. */}
          {r.inconsistentes > 0 ? (
            <div className="dash-aviso" role="alert">
              <strong>{r.inconsistentes}</strong>{' '}
              {r.inconsistentes === 1 ? 'pedido tem' : 'pedidos têm'} soma inconsistente
              (subtotal + taxa − desconto ≠ total). Os números abaixo podem não fechar.
            </div>
          ) : null}

          <div className="grade-stats">
            <StatTile
              rotulo="Faturamento"
              valor={formatarReais(r.faturamentoCents)}
              tom="marca"
              apoio="só os entregues"
              extra={<Variar v={dados.comparacao.faturamento} dinheiro />}
            />
            <StatTile
              rotulo="Pedidos entregues"
              valor={String(r.pedidos.entregues)}
              apoio={`${r.pedidos.emAndamento} em andamento`}
              extra={<Variar v={dados.comparacao.pedidos} />}
            />
            <StatTile
              rotulo="Ticket médio"
              valor={formatarReais(r.ticketMedioCents)}
              extra={<Variar v={dados.comparacao.ticketMedio} dinheiro />}
            />
            <StatTile
              rotulo="Média por dia"
              valor={formatarReais(r.mediaDiariaCents)}
              apoio={`${dados.periodo.dias} dias, inclusive os fechados`}
            />
          </div>

          {tabela ? (
            <TabelaDeTudo dados={dados} />
          ) : (
            <>
              <section className="dash-cartao">
                <SectionTitle tamanho="sm">Faturamento por dia</SectionTitle>
                <Tendencia serie={dados.serie} parcial={dados.periodo.ehHoje} />
              </section>

              <section className="dash-cartao">
                <SectionTitle tamanho="sm">Quando a loja vende</SectionTitle>
                <p className="dash-nota">
                  Hora do relógio da loja. A madrugada pertence ao turno da noite
                  anterior, como no fechamento do dia.
                </p>
                <MapaCalor calor={dados.calor} />
              </section>

              <div className="dash-colunas">
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">Por canal</SectionTitle>
                  <Barras fatias={dados.porCanal} />
                </section>
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">Por forma de pagamento</SectionTitle>
                  <Barras fatias={dados.porPagamento} />
                </section>
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">Entrega ou retirada</SectionTitle>
                  <Barras fatias={dados.porTipo} />
                </section>
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">Por bairro</SectionTitle>
                  <Barras fatias={dados.porBairro.slice(0, 8)} />
                </section>
              </div>

              <section className="dash-cartao">
                <SectionTitle tamanho="sm">O que faz o faturamento</SectionTitle>
                <p className="dash-nota">
                  Classe A são os produtos que somam os primeiros 80% do
                  faturamento; B vai até 95%; C é o resto. Muita coisa em C é
                  cardápio maior do que precisa.
                </p>
                <CurvaAbc abc={dados.abc} total={totalAbc} />
              </section>

              <div className="dash-colunas">
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">De onde vem o cliente</SectionTitle>
                  <TabelaOrigens origens={dados.origens} />
                </section>
                <section className="dash-cartao">
                  <SectionTitle tamanho="sm">Composição e entrega</SectionTitle>
                  <dl className="dash-lista">
                    <div><dt>Produtos</dt><dd>{formatarReais(r.composicao.produtosCents)}</dd></div>
                    <div><dt>Taxas de entrega</dt><dd>{formatarReais(r.composicao.taxasCents)}</dd></div>
                    <div><dt>Descontos</dt><dd>− {formatarReais(r.composicao.descontosCents)}</dd></div>
                    <div className="dash-lista__total">
                      <dt>Faturamento</dt><dd>{formatarReais(r.faturamentoCents)}</dd>
                    </div>
                    <div><dt>Cancelado</dt><dd>{formatarReais(r.perdidoCents)} ({r.pedidos.cancelamentoPct}%)</dd></div>
                    <div>
                      <dt>Distância média</dt>
                      <dd>
                        {dados.entrega.distanciaMediaKm === null
                          ? '—'
                          : `${dados.entrega.distanciaMediaKm.toFixed(1)} km`}
                      </dd>
                    </div>
                  </dl>
                </section>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Curva ABC como barras de UM eixo.
 *
 * O gráfico de Pareto clássico põe barra e linha acumulada em duas escalas no
 * mesmo desenho — e dois eixos y é a forma mais fácil de fazer alguém ler o
 * gráfico errado. Aqui o acumulado vai como texto na própria linha, que é onde
 * ele é lido de verdade, e a classe vira selo: a informação toda, um eixo só.
 */
function CurvaAbc({ abc, total }: { abc: ItemAbc[]; total: number }) {
  if (abc.length === 0) return <EmptyState titulo="Nenhum item vendido no período" />
  const maximo = Math.max(...abc.map((i) => i.faturamentoCents), 1)
  const porClasse = (c: 'A' | 'B' | 'C') => abc.filter((i) => i.classe === c).length

  return (
    <>
      <div className="dash-abc__resumo">
        {(['A', 'B', 'C'] as const).map((c) => (
          <span key={c} className={`dash-abc__selo dash-abc__selo--${c}`}>
            Classe {c}: {porClasse(c)} {porClasse(c) === 1 ? 'produto' : 'produtos'}
          </span>
        ))}
      </div>
      <ul className="dash-barras">
        {abc.slice(0, 15).map((i) => (
          <li key={i.produtoId ?? i.nome} className="dash-barra">
            <span className="dash-barra__alvo">
              <span className="dash-barra__nome">
                <span className={`dash-abc__marca dash-abc__marca--${i.classe}`} aria-hidden="true" />
                {i.nome}
              </span>
              <span className="dash-barra__trilha">
                <span
                  className={`dash-barra__preenchimento dash-barra__preenchimento--${i.classe}`}
                  style={{ width: `${Math.max(2, (i.faturamentoCents / maximo) * 100)}%` }}
                />
              </span>
              <span className="dash-barra__valor">
                {formatarReais(i.faturamentoCents)}
                <span className="dash-barra__apoio">
                  {i.quantidade} un · {i.acumuladoPct}% acum · classe {i.classe}
                </span>
              </span>
            </span>
          </li>
        ))}
      </ul>
      {abc.length > 15 ? (
        <p className="dash-nota">
          Mostrando os 15 maiores de {abc.length}. Os demais somam{' '}
          {formatarReais(total - abc.slice(0, 15).reduce((s, i) => s + i.faturamentoCents, 0))}.
        </p>
      ) : null}
    </>
  )
}

function TabelaOrigens({ origens }: { origens: Origem[] }) {
  if (origens.length === 0) return <EmptyState titulo="Nenhum acesso por QR no período" />
  return (
    <table className="dash-tabela">
      <thead>
        <tr>
          <th scope="col">Ponto</th>
          <th scope="col">Acessos</th>
          <th scope="col">Pedidos</th>
          <th scope="col">Conversão</th>
          <th scope="col">Faturamento</th>
        </tr>
      </thead>
      <tbody>
        {origens.map((o) => (
          <tr key={o.origem}>
            <th scope="row">{o.nome}</th>
            <td>{o.acessos}</td>
            <td>{o.pedidos}</td>
            {/* "0%" com acesso nenhum leria como desastre; a verdade é "não sabemos". */}
            <td>{o.conversaoPct === null ? <span title="Sem acesso registrado">—</span> : `${o.conversaoPct}%`}</td>
            <td>{formatarReais(o.faturamentoCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * A mesma informação em tabela.
 *
 * Não é extra de acessibilidade pendurado no fim: é como alguém confere um
 * número, copia para a planilha do contador, ou lê com leitor de tela. Um
 * gráfico sem tabela equivalente é informação que só existe para quem vê.
 */
function TabelaDeTudo({ dados }: { dados: Dados }) {
  return (
    <div className="dash-cartao">
      <table className="dash-tabela">
        <caption>Faturamento por dia</caption>
        <thead>
          <tr>
            <th scope="col">Dia</th>
            <th scope="col">Pedidos</th>
            <th scope="col">Faturamento</th>
            <th scope="col">Ticket médio</th>
          </tr>
        </thead>
        <tbody>
          {dados.serie.map((p) => (
            <tr key={p.dia}>
              <th scope="row">{ddmm(p.dia)}</th>
              <td>{p.pedidos}</td>
              <td>{formatarReais(p.faturamentoCents)}</td>
              <td>{p.pedidos > 0 ? formatarReais(p.ticketMedioCents) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {([
        ['Por canal', dados.porCanal],
        ['Por forma de pagamento', dados.porPagamento],
        ['Entrega ou retirada', dados.porTipo],
        ['Por bairro', dados.porBairro],
      ] as const).map(([titulo, fatias]) => (
        <table key={titulo} className="dash-tabela">
          <caption>{titulo}</caption>
          <thead>
            <tr>
              <th scope="col">Nome</th>
              <th scope="col">Pedidos</th>
              <th scope="col">Faturamento</th>
              <th scope="col">Fatia</th>
            </tr>
          </thead>
          <tbody>
            {fatias.map((f) => (
              <tr key={f.chave}>
                <th scope="row">{nomear(f.chave)}</th>
                <td>{f.pedidos}</td>
                <td>{formatarReais(f.faturamentoCents)}</td>
                <td>{f.fatiaPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}

      <table className="dash-tabela">
        <caption>Produtos, da maior para a menor venda</caption>
        <thead>
          <tr>
            <th scope="col">Produto</th>
            <th scope="col">Unidades</th>
            <th scope="col">Faturamento</th>
            <th scope="col">Acumulado</th>
            <th scope="col">Classe</th>
          </tr>
        </thead>
        <tbody>
          {dados.abc.map((i) => (
            <tr key={i.produtoId ?? i.nome}>
              <th scope="row">{i.nome}</th>
              <td>{i.quantidade}</td>
              <td>{formatarReais(i.faturamentoCents)}</td>
              <td>{i.acumuladoPct}%</td>
              <td><Badge tom={i.classe === 'A' ? 'marca' : 'neutro'}>{i.classe}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>

      <TabelaOrigens origens={dados.origens} />
    </div>
  )
}
